import { RecordAttributes, ControlId } from '@amzn/controllershiphub-typescript-client';
import {
  Button,
  ButtonDropdown,
  Spinner,
  Tabs,
  SpaceBetween,
  Header,
  Alert,
} from '@cloudscape-design/components';
import { useFlashbar } from 'src/common/hooks/useFlashbar';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useControls } from 'src/api/hooks/useControlData';
import ControlMetadataContent from 'src/common/ControlMetadataContent';
import PageLayout from '../common/PageLayout/PageLayout';
import { GridRef } from './ControlSummaryPageAGGrid';
import { useControlAuditLogs } from 'src/api/hooks';
import AuditTrailTable from '../AuditTrail/AuditTrailTable';
import { ControlFilterView } from 'src/components/Filter';
import { getButtonDropdownItems } from 'src/common/ControlSummaryTableConfig';
import { useControlMetadata } from 'src/api/hooks/useControlMetadata';
import { useSharedViews } from 'src/api/hooks/useSharedViews';
import { SharedViewsTable } from 'src/components/views/SharedViewsTable';
import { ExportsTable } from 'src/components/exports/ExportsTable';
import { OpenPOExportsTable } from 'src/components/exports/OpenPOExportsTable';
import { OpenPOExportModal } from 'src/components/exports/OpenPOExportModal';
import '../views/styles.scss';
import { SharedView } from 'src/components/views/ShareView';
import { ControlDataTabContent } from './ControlDataTabContent';
import { getLeaseTypeFromUrl } from 'src/api/hooks/getControlData';
import { LEASE_TYPES } from 'src/constants/grc5253';
import { ControlId as ControlIdConst } from 'src/constants/api';
import { ReportsSnapshotsForm } from '../reports/ReportsSnapshotsForm';
import AuditTrailPaginatedTable from 'src/components/AuditTrail/AuditTrailPaginatedTable';
import { HistoricalExecutionsPage } from 'src/components/pages/HistoricalExecutionsPage';
import { GRC5253AWSReportSnapshotsForm } from '../reports/GRC5253AWSReportSnapshotsForm';

/**
 * Check if a record is empty
 * @param {RecordAttributes[]} record - The record to check
 * @returns {boolean} True if the record is empty, false otherwise
 */
export const isRecordEmpty = (record: RecordAttributes[]): boolean => {
  // If the record is null or empty, consider it empty
  if (record == null || record.length === 0) return true;
  // Check if all attributes in the record are empty (empty string or null)
  return record.every((attr) =>
    Object.values(attr).every((value) => value === '' || value === null)
  );
};

/**
 * Filter out empty records from the data
 * @param {RecordAttributes[][]} data - The data to filter
 * @returns {RecordAttributes[][]} Filtered data with non-empty records
 */
export const filterAndCleanRecords = (data: RecordAttributes[][]): RecordAttributes[][] => {
  return data.filter((record) => !isRecordEmpty(record));
};

export const SummaryPage = () => {
  const { addFlashbarMessage, clearFlashbarMessages } = useFlashbar();
  const { data, isLoading: controlsLoading, error: controlsError } = useControls();
  const controls = data?.controls ?? [];
  const [activeTabId, setActiveTabId] = useState('table');
  const [activeSubTabId, setActiveSubTabId] = useState('gl-data');
  const { controlId } = useParams<{ controlId: string }>();
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [isBulkEditModalVisible, setIsBulkEditModalVisible] = useState(false);
  const {
    metadata: controlAttributesData,
    error: metadataError,
    setError,
  } = useControlMetadata(controlId);
  const safeControlId = controlId ?? '';
  const gridRef = useRef<GridRef>(null);
  const [dataFullyLoaded, setDataFullyLoaded] = useState(false);
  const loadingFlashIdRef = useRef<string | null>(null);
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setDataFullyLoaded(false);
    loadingFlashIdRef.current = null;
    return () => {
      clearFlashbarMessages();
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    };
  }, [controlId]);

  const handleFullyLoaded = (loaded: boolean, recordCount?: number) => {
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }
    setDataFullyLoaded(loaded);
    if (!loaded && recordCount && recordCount > 0) {
      const id = `data-loading-${Date.now()}`;
      loadingFlashIdRef.current = id;
      addFlashbarMessage({
        id,
        type: 'info',
        loading: true,
        content: `Loading data... ${recordCount.toLocaleString()} records loaded so far`,
        dismissible: true,
      });
    } else if (loaded && loadingFlashIdRef.current) {
      clearFlashbarMessages();
      loadingFlashIdRef.current = null;
      const successId = `data-loaded-${Date.now()}`;
      addFlashbarMessage({
        id: successId,
        type: 'success',
        content: `All ${recordCount?.toLocaleString() ?? ''} records loaded`,
        dismissible: true,
      });
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(() => clearFlashbarMessages(), 5000);
    }
  };
  const {
    data: auditData,
    refetch,
    isPending: isPendingAudits,
    isFetching,
  } = useControlAuditLogs({ controlId: controlId });

  const {
    sharedViews,
    isLoadingSharedViews,
    handleAcceptView,
    handleDeclineView,
    loadSharedViews,
  } = useSharedViews(controlId!);

  // Handle selection changes in the grid
  const handleSelectionChange = (rows: any[]) => {
    setSelectedRows(rows);
  };

  // Handle bulk edit modal visibility
  const handleBulkEditModalVisibility = (visible: boolean) => {
    setIsBulkEditModalVisible(visible);
  };

  // Handle successful bulk edit
  const handleBulkEditSuccess = (updatedRecords: any[]) => {
    console.log('[SummaryPage] Bulk edit successful, updating grid with saved values...');

    try {
      if (gridRef.current && gridRef.current.api) {
        // Get all current data from the grid
        const allData: any[] = [];
        gridRef.current.api.forEachNode((node: any) => {
          allData.push(node.data);
        });

        // Create a map of updated records for quick lookup
        const updatedRecordsMap = new Map(
          updatedRecords.map((record) => [record[controlDatasetKey], record])
        );

        // Update the data while preserving all records
        const newData = allData.map((row) => {
          const updatedRecord = updatedRecordsMap.get(row[controlDatasetKey]);
          return updatedRecord || row;
        });

        // Update the grid's data
        gridRef.current.api.setGridOption('rowData', newData);

        // Force a complete refresh
        gridRef.current.api.refreshCells({ force: true });

        console.log(
          '[SummaryPage] Grid updated with bulk edit changes:',
          updatedRecords.length,
          'records'
        );
      }
    } catch (error) {
      console.error('[SummaryPage] Error updating grid with bulk edit changes:', error);
      addFlashbarMessage({
        type: 'error',
        header: 'Error',
        content: 'Failed to update grid with bulk edit changes. Please refresh the page.',
        dismissible: true,
        id: 'bulk_edit_error',
      });
    }
  };

  // Check if additional data is enabled for this control
  const isAdditionalDataEnabled = controlId === 'grc18956';

  // Get the control dataset key from metadata
  const controlDatasetKey = useMemo(() => {
    if (!controlAttributesData?.attributesMetadata) return 'id';
    const idField = controlAttributesData.controlDatasetKey;
    return idField!;
  }, [controlAttributesData]);

  // Memoize the control object based on the controlId
  const control = useMemo(() => {
    // Find the control in the controls array that matches the controlId, or return null if not found
    return controls.find((c) => c.controlId === controlId) || null;
  }, [controls, controlId]);

  const getPendingViewsCount = (views: SharedView[] | undefined) => {
    if (!views) return 0;
    return views.filter((view) => view.status === 'PENDING').length;
  };

  const leaseType = getLeaseTypeFromUrl();

  const [showOpenPOExportModal, setShowOpenPOExportModal] = useState(false);
  const [refreshOpenPOExports, setRefreshOpenPOExports] = useState(0);
  const [exportActiveFilters, setExportActiveFilters] = useState<Record<string, string[]>>({});

  const handleDropdownAction = (event: any) => {
    if (event.detail.id === 'export-data') {
      setShowOpenPOExportModal(true);
    }
  };

  const dropdownItems = useMemo(() => {
    // For GRC5253 SDO, use separate reference items
    if (control?.controlId === ControlIdConst.GRC5253 && leaseType === LEASE_TYPES.SDO) {
      return getButtonDropdownItems('grc5253-sdo', leaseType);
    }
    return getButtonDropdownItems(control?.controlId || null, leaseType);
  }, [control?.controlId, leaseType]);

  // Handle metadata errors
  useEffect(() => {
    if (metadataError) {
      addFlashbarMessage({
        type: 'error',
        header: 'Error',
        content: metadataError,
        dismissible: true,
        dismissLabel: 'Dismiss message',
        onDismiss: () => {
          setError(null);
          clearFlashbarMessages();
        },
        id: 'metadata_error',
      });
    }
  }, [metadataError, addFlashbarMessage, setError]);

  // Load shared views when controlId changes
  useEffect(() => {
    if (controlId) {
      loadSharedViews();
    }
  }, [controlId, loadSharedViews]);

  // Handle controls errors
  useEffect(() => {
    if (controlsError?.message) {
      addFlashbarMessage({
        type: 'error',
        header: 'Error',
        content: `Failed to load controls: ${controlsError.message}.`,
        dismissible: true,
        dismissLabel: 'Dismiss message',
        onDismiss: () => window.location.reload(),
        id: 'controls_error',
      });
    }
  }, [controlsError, addFlashbarMessage]);

  if (controlsLoading) {
    return <Spinner />;
  }

  if (controlsError) {
    return <PageLayout />;
  }

  const location = useLocation();
  console.log(controlDatasetKey);

  // Show unauthorized access message if needed
  // GRC5253 has separate authorization for AWS vs SDO leases:
  // - userAuthorized: true if user has AWS team bindle lock (controls AWS button/page access)
  // - storesUserAuthorized: true if user has SDO team bindle lock (controls SDO button/page access)
  // Check specific permission based on leaseType URL parameter to prevent URL manipulation bypass.
  // For GRC5253, leaseType must be specified - direct navigation without leaseType is denied.
  const hasAccess =
    safeControlId === ControlIdConst.GRC5253
      ? leaseType === LEASE_TYPES.AWS
        ? control?.userAuthorized
        : leaseType === LEASE_TYPES.SDO
          ? (control as any)?.storesUserAuthorized
          : false
      : control?.userAuthorized || (control as any)?.storesUserAuthorized;
  if (control && !hasAccess && !control?.externalLink) {
    addFlashbarMessage({
      type: 'error',
      header: 'Access Denied',
      content:
        'You are not authorized to access this control. ' +
        'Please contact your administrator to request Permission',
      dismissible: false,
      id: 'unauthorized-access',
    });
  }

  return controls.length > 0 && hasAccess ? (
    <PageLayout disableOverlap defaultPadding={true}>
      <div style={{ position: 'relative' }}>
        <Tabs
          activeTabId={activeTabId}
          onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
          tabs={[
            {
              label: 'Control Data',
              id: 'table',
              content: (
                <ControlDataTabContent
                  controlId={safeControlId}
                  isAdditionalDataEnabled={isAdditionalDataEnabled}
                  loadDataByDefault={controlAttributesData?.loadDataByDefault ?? true}
                  controlAttributesData={controlAttributesData}
                  controlDatasetKey={controlDatasetKey}
                  selectedRows={selectedRows}
                  isBulkEditModalVisible={isBulkEditModalVisible}
                  onSelectionChange={handleSelectionChange}
                  onBulkEditModalVisibility={handleBulkEditModalVisibility}
                  onBulkEditSuccess={handleBulkEditSuccess}
                  activeSubTabId={activeSubTabId}
                  setActiveSubTabId={setActiveSubTabId}
                  onNavigateToExports={() => setActiveTabId('exports')}
                  onOpenPOExport={(activeFilters: Record<string, string[]>) => {
                    setExportActiveFilters(activeFilters);
                    setShowOpenPOExportModal(true);
                  }}
                  ref={gridRef}
                  onFullyLoaded={handleFullyLoaded}
                />
              ),
            },
            {
              label: 'Information',
              id: 'information',
              content: (
                <ControlMetadataContent
                  controlAttributesData={controlAttributesData}
                  control={control}
                />
              ),
            },
            ...(controlId != 'grc18956'
              ? [
                  {
                    label: 'Audit Trail',
                    id: 'auditTrail',
                    content: auditData?.auditLogs && (
                      <AuditTrailPaginatedTable controlId={controlId!} />
                    ),
                  },
                ]
              : []),
            {
              label: (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  Shared Views
                  {getPendingViewsCount(sharedViews) > 0 && (
                    <span className={'notification-badge'}>
                      {getPendingViewsCount(sharedViews)}
                    </span>
                  )}
                </div>
              ),
              id: 'sharedViews',
              content: (
                <SharedViewsTable
                  sharedViews={sharedViews}
                  onAccept={handleAcceptView}
                  onDecline={handleDeclineView}
                  loading={isLoadingSharedViews}
                />
              ),
            },
            ...(controlAttributesData?.enableSnapshotView === true &&
            controlId === ControlIdConst.GRC5253 &&
            leaseType === LEASE_TYPES.SDO
              ? [
                  {
                    label: 'Reports and Snapshots',
                    id: 'reportsSnapshots',
                    content: <ReportsSnapshotsForm />,
                  },
                ]
              : []),
            ...(controlAttributesData?.enableSnapshotView === true &&
            controlId === ControlIdConst.GRC5253 &&
            leaseType === LEASE_TYPES.AWS
              ? [
                  {
                    label: 'Reports and Snapshots',
                    id: 'reportsSnapshots',
                    content: <GRC5253AWSReportSnapshotsForm />,
                  },
                ]
              : []),
            ...(controlId === 'grc18956'
              ? [
                  {
                    label: 'Exports',
                    id: 'exports',
                    content: (
                      <SpaceBetween size="l">
                        <SpaceBetween size="s">
                          <Alert>
                            The status of exports is static, please click the refresh button to see
                            the most up to date status of your export.
                          </Alert>
                          <Alert>GL Data Exports also include WebMEC exports.</Alert>
                        </SpaceBetween>
                        <ExportsTable controlId={safeControlId} />
                        <OpenPOExportsTable controlId={safeControlId} key={refreshOpenPOExports} />
                      </SpaceBetween>
                    ),
                  },
                ]
              : []),
            ...(controlAttributesData?.enableHistoricalSnapshotsPage === true
              ? [
                  {
                    label: 'Historical Snapshots',
                    id: 'historicalSnapshots',
                    content: <HistoricalExecutionsPage />,
                  },
                ]
              : []),
          ]}
        />
        {activeTabId === 'table' && (
          <div
            style={{
              position: 'absolute',
              top: '2px',
              right: '0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              zIndex: 1,
            }}
          >
            <Button
              variant="inline-icon"
              iconName="download"
              disabled={controlId === 'grc18956' || !dataFullyLoaded}
              onClick={() => {
                gridRef.current?.exportDataAsCsv();
              }}
            >
              {!dataFullyLoaded ? 'Loading...' : 'Export'}
            </Button>
            <ButtonDropdown
              items={dropdownItems}
              expandToViewport
              onItemClick={handleDropdownAction}
            >
              References
            </ButtonDropdown>
            {controlId !== 'grc18956' && (
              <>
                <Button
                  disabled={selectedRows.length === 0}
                  onClick={() => handleBulkEditModalVisibility(true)}
                >
                  Bulk Edit
                </Button>

                <Button
                  variant="primary"
                  disabled={!dataFullyLoaded}
                  onClick={() => gridRef.current?.saveChanges()}
                >
                  Save Changes
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {showOpenPOExportModal && (
        <OpenPOExportModal
          controlId={safeControlId}
          activeFilters={exportActiveFilters}
          onClose={() => setShowOpenPOExportModal(false)}
          onExportCreated={() => {
            setShowOpenPOExportModal(false);
            // Navigate to exports tab and refresh the list
            setActiveTabId('exports');
            setRefreshOpenPOExports((prev) => prev + 1);
          }}
        />
      )}
    </PageLayout>
  ) : (
    <Spinner />
  );
};
